/**
 * O que a barra de formatação insere nos campos `front` e `back`.
 * A barra insere `**`, `*`, `==`, uma linha `- ` e `$...$`. `imagem`
 * permanece em `FORMATOS_CARTAO` / `BOTOES_FORMATO` porque a barra ainda
 * mostra esse comando, mas este módulo não o insere. O comando abre o
 * alvo de arquivo da face (`AlvoDeImagemDaFace`). Um `![alt](url)` já
 * presente no campo — digitado à mão ou escrito depois de um envio — não
 * é alterado por este módulo; `CardMarkdown` continua a renderizá-lo.
 * O campo continua uma `string`.
 */
export const FORMATOS_CARTAO = [
  'enfase-forte',
  'enfase',
  'destaque',
  'lista',
  'formula',
  'imagem',
] as const;

export type FormatoCartao = (typeof FORMATOS_CARTAO)[number];

/** Comandos da barra que escrevem no campo. `imagem` não é um deles: abre o alvo de arquivo. */
export type FormatoInserivel = Exclude<FormatoCartao, 'imagem'>;

export interface BotaoFormato {
  id: FormatoCartao;
  nome: string;
}

export const BOTOES_FORMATO: readonly BotaoFormato[] = [
  { id: 'enfase-forte', nome: 'Ênfase forte' },
  { id: 'enfase', nome: 'Ênfase' },
  { id: 'destaque', nome: 'Destaque' },
  { id: 'lista', nome: 'Lista' },
  { id: 'formula', nome: 'Fórmula' },
  { id: 'imagem', nome: 'Imagem' },
];

export interface TrechoInserido {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

const ENVOLTORIO: Record<
  'enfase-forte' | 'enfase' | 'destaque' | 'formula',
  { open: string; close: string; amostra: string }
> = {
  'enfase-forte': { open: '**', close: '**', amostra: 'ênfase forte' },
  enfase: { open: '*', close: '*', amostra: 'ênfase' },
  destaque: { open: '==', close: '==', amostra: 'destaque' },
  formula: { open: '$', close: '$', amostra: 'fórmula' },
};

function limitar(posicao: number, tamanho: number): number {
  if (!Number.isFinite(posicao) || posicao < 0) return 0;
  if (posicao > tamanho) return tamanho;
  return posicao;
}

function intervalo(
  value: string,
  selectionStart: number,
  selectionEnd: number,
): [number, number] {
  const inicio = limitar(selectionStart, value.length);
  const fim = limitar(selectionEnd, value.length);
  return inicio <= fim ? [inicio, fim] : [fim, inicio];
}

function envolver(
  value: string,
  start: number,
  end: number,
  open: string,
  close: string,
  amostra: string,
): TrechoInserido {
  const selecionado = value.slice(start, end);
  const miolo = selecionado.length > 0 ? selecionado : amostra;
  const next = value.slice(0, start) + open + miolo + close + value.slice(end);
  const selectionStart = start + open.length;
  return {
    value: next,
    selectionStart,
    selectionEnd: selectionStart + miolo.length,
  };
}

/** Linhas cobertas pela seleção. Um fim colado na quebra não puxa a linha seguinte. */
function intervaloDeLinhas(value: string, start: number, end: number): [number, number] {
  const from = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  let cursor = end;
  if (cursor > start && value[cursor - 1] === '\n') cursor -= 1;
  const quebra = value.indexOf('\n', cursor);
  const to = quebra === -1 ? value.length : quebra;
  return [from, to];
}

function inserirLista(value: string, start: number, end: number): TrechoInserido {
  const [from, to] = intervaloDeLinhas(value, start, end);
  const bloco = value.slice(from, to);
  const linhas = bloco.length === 0 ? [''] : bloco.split('\n');
  const marcadas = linhas.map((linha) => (linha.startsWith('- ') ? linha : `- ${linha}`));
  const nextBloco = marcadas.join('\n');
  return {
    value: value.slice(0, from) + nextBloco + value.slice(to),
    selectionStart: from,
    selectionEnd: from + nextBloco.length,
  };
}

/**
 * Insere um formato do conjunto fechado no campo, no lugar da seleção.
 * Seleção vazia deixa uma amostra selecionada para o autor substituir
 * sem apagar o marcador.
 */
export function inserirFormato(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  formato: FormatoInserivel,
): TrechoInserido {
  const texto = value ?? '';
  const [start, end] = intervalo(texto, selectionStart, selectionEnd);
  if (formato === 'lista') return inserirLista(texto, start, end);
  const marca = ENVOLTORIO[formato];
  return envolver(texto, start, end, marca.open, marca.close, marca.amostra);
}
